import React, { useState, useEffect } from 'react';
import { Badge } from './components/ui/badge';
import { Button } from './components/ui/button';
import { Download, RefreshCw, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SESSION_COLORS = {
  FS: 'bg-blue-100 text-blue-800 border-blue-300',
  HS1: 'bg-purple-100 text-purple-800 border-purple-300',
  HS2: 'bg-orange-100 text-orange-800 border-orange-300',
  '6WK': 'bg-teal-100 text-teal-800 border-teal-300',
  FLEX: 'bg-pink-100 text-pink-800 border-pink-300',
  CIT: 'bg-gray-200 text-gray-800 border-gray-400',
};

const DIVISION_TABS = [
  { label: 'All', codes: null },
  { label: 'Nursery', codes: ['N02', 'N03'] },
  { label: 'Pre-K Girls', codes: ['N04', 'N05'] },
  { label: 'Pre-K Boys', codes: ['N07', 'N08'] },
  { label: 'K Girls', codes: ['N10', 'N11', 'N12', 'N13'] },
  { label: 'K Boys', codes: ['N14', 'N15', 'N16'] },
  { label: '1st/2nd Boys', codes: ['B01', 'B02', 'B03', 'B04', 'B05', 'B06', 'B07'] },
  { label: '1st/2nd Girls', codes: ['G01', 'G02', 'G03', 'G04', 'G05', 'G06', 'G07'] },
  { label: '3rd/4th Boys', codes: ['B08', 'B09', 'B10', 'B11'] },
  { label: '3rd/4th Girls', codes: ['G08', 'G09', 'G10', 'G11'] },
  { label: 'Nav Girls', codes: ['G12', 'G13', 'G14', 'G15'] },
  { label: 'Nav Boys', codes: ['B12', 'B13', 'B14', 'B15'] },
  { label: 'CIT', codes: ['CIT'] },
];

function enrollColor(present, cap) {
  if (cap === 0) return '';
  const avail = cap - present;
  if (avail < 0) return 'bg-purple-200';
  if (avail === 0) return 'bg-red-200';
  const pct = avail / cap;
  if (pct <= 0.15) return 'bg-orange-100';
  if (pct <= 0.3) return 'bg-yellow-50';
  return 'bg-green-50';
}

function ReportPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/availability/report`);
      if (res.ok) {
        const data = await res.json();
        setReport(data);
        setLastRefresh(new Date());
      }
    } catch (e) {
      console.error('Failed to fetch report:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const handleDownloadCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/api/availability/export/csv`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `availability_report_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV download failed:', e);
    }
  };

  const selectedTab = DIVISION_TABS[activeTab];
  const filteredGroups = report?.groups?.filter(g =>
    selectedTab.codes === null || selectedTab.codes.includes(g.code)
  );

  return (
    <div className="h-screen h-[100dvh] flex flex-col bg-white" data-testid="report-page">
      <style>{`
        .report-scroll::-webkit-scrollbar { width: 20px; height: 20px; }
        .report-scroll::-webkit-scrollbar-track { background: #e5e7eb; }
        .report-scroll::-webkit-scrollbar-thumb { background: #6b7280; border-radius: 10px; border: 3px solid #e5e7eb; }
        .report-scroll::-webkit-scrollbar-thumb:hover { background: #4b5563; }
        .report-scroll::-webkit-scrollbar-corner { background: #e5e7eb; }
        .report-scroll { scrollbar-width: auto; scrollbar-color: #6b7280 #e5e7eb; }
      `}</style>

      {/* Fixed header bar */}
      <div className="flex flex-wrap items-center justify-between px-3 py-2 bg-[#1a3a6b] text-white shrink-0 gap-2 z-50">
        <div className="flex items-center gap-2 min-w-0">
          <BarChart3 className="w-5 h-5 shrink-0" />
          <h1 className="text-sm sm:text-lg font-bold truncate">2026 Rolling River Availability</h1>
          {lastRefresh && (
            <span className="text-[10px] text-blue-200 hidden sm:inline">
              {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={fetchReport} disabled={loading} className="bg-white/10 border-white/30 text-white hover:bg-white/20 text-xs" data-testid="report-refresh">
            <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={handleDownloadCSV} className="bg-white/10 border-white/30 text-white hover:bg-white/20 text-xs" data-testid="report-download-csv">
            <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {/* Division tabs */}
      <div className="flex gap-1 px-2 py-1.5 bg-gray-100 border-b border-gray-300 overflow-x-auto shrink-0" data-testid="division-tabs">
        {DIVISION_TABS.map((tab, i) => (
          <button
            key={tab.label}
            onClick={() => setActiveTab(i)}
            className={`px-2 py-1 text-[11px] font-medium rounded whitespace-nowrap transition-colors ${
              activeTab === i
                ? 'bg-[#1a3a6b] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-200 border border-gray-300'
            }`}
            data-testid={`tab-${tab.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Scrollable report */}
      <div
        className="flex-1 report-scroll"
        style={{
          overflow: 'scroll',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain'
        }}
      >
        {loading && !report ? (
          <div className="flex items-center justify-center h-64 text-gray-500">Loading report...</div>
        ) : report && filteredGroups ? (
          <ReportTable report={report} groups={filteredGroups} />
        ) : (
          <div className="flex items-center justify-center h-64 text-gray-500">Failed to load report</div>
        )}
      </div>
    </div>
  );
}

function ReportTable({ report, groups }) {
  const { calendar } = report;

  const COL1_W = 30;
  const COL2_W = 110;

  return (
    <div className="text-[10px] leading-tight">
      <table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>
          <tr className="bg-[#1a3a6b] text-white">
            <th className="border border-[#2b5ea7] px-1 py-1 text-left bg-[#1a3a6b]"
                style={{ position: 'sticky', top: 0, left: 0, zIndex: 40, minWidth: COL1_W }}>#</th>
            <th className="border border-[#2b5ea7] px-1 py-1 text-left bg-[#1a3a6b]"
                style={{ position: 'sticky', top: 0, left: COL1_W, zIndex: 40, minWidth: COL2_W }}>Camp Group</th>
            {calendar.map(d => (
              <th key={d.day_num} colSpan={2}
                  className="border border-[#2b5ea7] px-0 py-1 text-center bg-[#1a3a6b] border-l-2 border-l-black"
                  style={{ position: 'sticky', top: 0, zIndex: 30, minWidth: 48 }}>
                <div className="font-bold">Day {d.day_num}</div>
                <div className="font-normal text-blue-200 text-[9px]">{d.display} {d.weekday}</div>
              </th>
            ))}
          </tr>
          <tr className="bg-[#2b5ea7] text-white text-[9px]">
            <th className="border border-[#3b6eb7] bg-[#2b5ea7]"
                style={{ position: 'sticky', top: 38, left: 0, zIndex: 40 }}></th>
            <th className="border border-[#3b6eb7] bg-[#2b5ea7]"
                style={{ position: 'sticky', top: 38, left: COL1_W, zIndex: 40 }}></th>
            {calendar.map(d => (
              <React.Fragment key={d.day_num}>
                <th className="border border-[#3b6eb7] px-0.5 py-0.5 text-center bg-[#2b5ea7] border-l-2 border-l-black"
                    style={{ position: 'sticky', top: 38, zIndex: 30, width: 24 }}>Enr</th>
                <th className="border border-[#3b6eb7] px-0.5 py-0.5 text-center bg-[#2b5ea7]"
                    style={{ position: 'sticky', top: 38, zIndex: 30, width: 24 }}>Avl</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          <TotalRow calendar={calendar} groups={groups} col1w={COL1_W} />

          {groups.map((g, gi) => {
            const grade = g.code.startsWith('N') ? '' : g.code.startsWith('B') || g.code.startsWith('G') ? g.code.replace(/[BG]/, '').replace(/^0/, '') : '';
            const bgClass = gi % 2 === 0 ? 'bg-white' : 'bg-gray-50';
            const bgColor = gi % 2 === 0 ? '#ffffff' : '#f9fafb';
            return (
              <tr key={g.code} className={bgClass}>
                <td className="border px-1 py-0.5 text-center text-black font-medium"
                    style={{ position: 'sticky', left: 0, zIndex: 10, backgroundColor: bgColor }}>{grade}</td>
                <td className="border px-1 py-0.5 font-semibold whitespace-nowrap text-black"
                    style={{ position: 'sticky', left: COL1_W, zIndex: 10, backgroundColor: bgColor }}>{g.display}</td>
                {calendar.map(d => {
                  const gd = d.groups[g.code] || {};
                  const present = gd.present || 0;
                  const cap = gd.cap || 0;
                  const avail = cap - present;
                  const ec = enrollColor(present, cap);
                  return (
                    <React.Fragment key={d.day_num}>
                      <td className={`border px-0.5 py-0.5 text-center font-medium text-black border-l-2 border-l-black`}>{present}</td>
                      <td className={`border px-0.5 py-0.5 text-center text-black ${ec}`}>{avail}</td>
                    </React.Fragment>
                  );
                })}
              </tr>
            );
          })}

          <TotalRow calendar={calendar} groups={groups} col1w={COL1_W} />

          <tr><td colSpan={2 + 38 * 2} className="h-4 border-0" /></tr>
          <tr className="bg-gray-200 font-bold">
            <td className="border px-1 py-1" colSpan={2}
                style={{ position: 'sticky', left: 0, zIndex: 10, backgroundColor: '#e5e7eb' }}>Session Breakdown by Day</td>
            {calendar.map(d => (
              <td key={d.day_num} colSpan={2} className="border px-0.5 py-1 text-center text-[9px] text-gray-600 border-l-2 border-l-black">D{d.day_num}</td>
            ))}
          </tr>
          {['FS', 'HS1', 'HS2', '6WK', 'FLEX', 'CIT'].map(st => (
            <tr key={st} className="bg-gray-50">
              <td className="border px-1 py-0.5" colSpan={2}
                  style={{ position: 'sticky', left: 0, zIndex: 10, backgroundColor: '#f9fafb' }}>
                <Badge variant="outline" className={`text-[9px] ${SESSION_COLORS[st]}`}>{st}</Badge>
              </td>
              {calendar.map(d => (
                <td key={d.day_num} colSpan={2} className="border px-0.5 py-0.5 text-center border-l-2 border-l-black">
                  {d.session_breakdown[st] || 0}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TotalRow({ calendar, groups, col1w }) {
  return (
    <tr className="bg-gray-800 text-white font-bold">
      <td className="border border-gray-600 px-1 py-0.5"
          style={{ position: 'sticky', left: 0, zIndex: 10, backgroundColor: '#1f2937' }}></td>
      <td className="border border-gray-600 px-1 py-0.5"
          style={{ position: 'sticky', left: col1w, zIndex: 10, backgroundColor: '#1f2937' }}>TOTAL</td>
      {calendar.map(d => {
        const totalCap = groups.reduce((sum, g) => sum + (d.groups[g.code]?.cap || 0), 0);
        const totalPresent = groups.reduce((sum, g) => sum + (d.groups[g.code]?.present || 0), 0);
        const totalAvail = totalCap - totalPresent;
        return (
          <React.Fragment key={d.day_num}>
            <td className="border border-gray-600 px-0.5 py-0.5 text-center border-l-2 border-l-black">{totalPresent}</td>
            <td className="border border-gray-600 px-0.5 py-0.5 text-center">{totalAvail}</td>
          </React.Fragment>
        );
      })}
    </tr>
  );
}

export default ReportPage;
